/**
 * Catálogo de recorridos MuniBus.
 * - activo: false = no aparece en landing/chips.
 * - patente/busId: fallback si no hay asignación en GET /api/asignaciones/buses.
 * - mapColors: trazado en mapa (ida más intenso, vuelta más claro) — alineado a chips T1…T5.
 */

/** @typedef {{ ida: string; vuelta: string }} RecorridoMapColors */

/**
 * Pastilla de la landing (munibus.cl), alineada a paine.cl: T1A azul oscuro, T1B celeste más claro,
 * T5A violeta, T5B magenta.
 * @typedef {{ background: string; foreground?: string }} RecorridoLandingColors
 */

/** Colores por familia de línea (misma paleta que chips: azul, naranja, rojo, verde, violeta). */
const MAP_COLORS_T1 = { ida: '#005E9A', vuelta: '#38BDF8' };
/** T2: naranja más claro (landing + mapa ida). */
const MAP_COLORS_T2 = { ida: '#F97316', vuelta: '#FDBA74' };
const MAP_COLORS_T3 = { ida: '#B91C1C', vuelta: '#FCA5A5' };
const MAP_COLORS_T4 = { ida: '#166534', vuelta: '#86EFAC' };
const MAP_COLORS_T5 = { ida: '#5B21B6', vuelta: '#C4B5FD' };

export const DEFAULT_RECORRIDO_ID = 't2';

/** Catálogo completo (visibilidad en landing/chips puede venir de GET /api/trayectos). */
export const RECORRIDOS = [
  {
    id: 't1a',
    codigo: 'T1A',
    nombre: 'Recorrido T1A - Rangue',
    lineaEtiqueta: 'Línea Norte',
    variant: 'primary',
    mapColors: MAP_COLORS_T1,
    activo: true,
    kmlBySentido: {
      ida: '/rutas/t1_ida.kml',
      vuelta: '/rutas/t1_vuelta.kml',
    },
    patente: 'TDDF 89',
    busId: 'TDDF 89',
    landingColors: { background: '#005E9A', foreground: '#ffffff' },
  },
  {
    id: 't1b',
    codigo: 'T1B',
    nombre: 'Recorrido T1B - Rangue',
    lineaEtiqueta: 'Línea Norte',
    variant: 'primary',
    mapColors: MAP_COLORS_T1,
    activo: true,
    kmlBySentido: {
      ida: '/rutas/t1_ida.kml',
      vuelta: '/rutas/t1_vuelta.kml',
    },
    patente: 'VBCZ 70',
    busId: 'VBCZ 70',
    /** Celeste más claro que T1A (paine / manual operativo). */
    landingColors: { background: '#0EA5E9', foreground: '#ffffff' },
  },
  {
    id: 't2',
    codigo: 'T2',
    nombre: 'Recorrido T2 - Águila Sur',
    lineaEtiqueta: 'Línea Sur',
    variant: 'orange',
    mapColors: MAP_COLORS_T2,
    activo: true,
    kmlBySentido: {
      ida: '/rutas/t2_ida.kml',
      vuelta: '/rutas/t2_vuelta.kml',
    },
    patente: 'VBCZ 60',
    busId: 'VBCZ 60',
    landingColors: { background: MAP_COLORS_T2.ida, foreground: '#ffffff' },
  },
  {
    id: 't3',
    codigo: 'T3',
    nombre: 'Recorrido T3 - Culitrín',
    lineaEtiqueta: 'Línea Express',
    variant: 'destructive',
    mapColors: MAP_COLORS_T3,
    activo: true,
    kmlBySentido: {
      ida: '/rutas/t3_ida.kml',
      vuelta: '/rutas/t3_vuelta.kml',
    },
    patente: null,
    busId: null,
    landingColors: { background: MAP_COLORS_T3.ida, foreground: '#ffffff' },
  },
  {
    id: 't4',
    codigo: 'T4',
    nombre: 'Recorrido T4 - Tránsito',
    lineaEtiqueta: 'Línea Verde',
    variant: 'secondary',
    mapColors: MAP_COLORS_T4,
    activo: true,
    kmlBySentido: {
      ida: '/rutas/t4_ida.kml',
      vuelta: '/rutas/t4_vuelta.kml',
    },
    patente: 'TDDD 95',
    busId: 'TDDD 95',
    landingColors: { background: MAP_COLORS_T4.ida, foreground: '#ffffff' },
  },
  {
    id: 't5a',
    codigo: 'T5A',
    nombre: 'Recorrido T5A - Nos',
    lineaEtiqueta: 'Línea Perimetral',
    variant: 'violet',
    mapColors: MAP_COLORS_T5,
    activo: true,
    kmlBySentido: {
      ida: '/rutas/t5a_ida.kml',
      vuelta: '/rutas/t5a_vuelta.kml',
    },
    patente: 'SDWY 44',
    busId: 'SDWY 44',
    landingColors: { background: MAP_COLORS_T5.ida, foreground: '#ffffff' },
  },
  {
    id: 't5b',
    codigo: 'T5B',
    nombre: 'Recorrido T5B - Nos',
    lineaEtiqueta: 'Línea Perimetral',
    variant: 'violet',
    mapColors: MAP_COLORS_T5,
    activo: true,
    kmlBySentido: {
      ida: '/rutas/t5b_ida.kml',
      vuelta: '/rutas/t5b_vuelta.kml',
    },
    patente: 'HYDC 40',
    busId: 'HYDC 40',
    /** Magenta distinto del violeta T5A (paine / manual operativo). */
    landingColors: { background: '#C026D3', foreground: '#ffffff' },
  },
];

export const RECORRIDOS_BY_ID = Object.fromEntries(RECORRIDOS.map((r) => [r.id, r]));

/** Si un recorrido no define mapColors (legacy), usar tonos T1. */
const FALLBACK_MAP_COLORS = MAP_COLORS_T1;

/**
 * Colores del trazado en mapa (ida / vuelta) para el recorrido activo.
 * @param {{ mapColors?: RecorridoMapColors } | null | undefined} recorrido
 * @returns {RecorridoMapColors}
 */
export function getMapColorsForRecorrido(recorrido) {
  const c = recorrido?.mapColors;
  if (c?.ida && c?.vuelta) return c;
  return FALLBACK_MAP_COLORS;
}

const FALLBACK_LANDING = { background: '#005E9A', foreground: '#ffffff' };

/**
 * Colores de la pastilla en la landing por recorrido.
 * @param {{ landingColors?: RecorridoLandingColors } | null | undefined} recorrido
 * @returns {{ background: string; foreground: string }}
 */
export function getLandingCardColors(recorrido) {
  const lc = recorrido?.landingColors;
  if (lc?.background) {
    return {
      background: lc.background,
      foreground: lc.foreground || '#ffffff',
    };
  }
  return FALLBACK_LANDING;
}

/** Solo recorridos operativos en UI (landing, chips). */
export const RECORRIDOS_ACTIVOS = RECORRIDOS.filter((r) => r.activo !== false);

export function normalizeRecorridoId(raw) {
  if (raw == null || typeof raw !== 'string') return null;
  let s = raw.trim().toLowerCase();
  if (!s) return null;
  if (s === 't1') s = 't1a';
  if (s === 't5') s = 't5a';
  if (RECORRIDOS_BY_ID[s]) return s;
  const digits = s.replace(/^t/i, '');
  const guess = `t${digits}`;
  return RECORRIDOS_BY_ID[guess] ? guess : null;
}

/**
 * @param {string | null} fromUrl
 * @param {string | null} storedId
 */
export function resolveRecorrido(fromUrl, storedId) {
  /** No filtrar por `activo` estático: la visibilidad operativa viene de GET /api/trayectos (overlay si inactivo). */
  const pick = (raw) => {
    const id = normalizeRecorridoId(raw);
    if (!id) return null;
    const r = RECORRIDOS_BY_ID[id];
    return r || null;
  };
  const a = pick(fromUrl);
  if (a) return a;
  const b = pick(storedId);
  if (b) return b;
  return RECORRIDOS_BY_ID[DEFAULT_RECORRIDO_ID];
}
